import orgApi from '@api/orgApi';
import ItemEditor from '@components/itemEditor';
import OrgSelect from '@components/orgSelect';
import OrgUserList from '@components/orgUserList';
import useAuth from '@hooks/useAuth';
import useCanCreateInTenant from '@hooks/useCanCreateInTenant';
import { useTranslator } from '@i18n';
import { TextInput } from '@mantine/core';
import type { Org } from '@models/org';
import { createFileRoute } from '@tanstack/react-router';
import { createForm, LoadingBoundary } from 'cross-state/react';

export const Route = createFileRoute('/profile/groups/$id')({
  component: GroupEditor,
  /** The organization a new group is created in. Existing groups carry their parent in the payload. */
  validateSearch(search): { orgId?: string } {
    return typeof search.orgId === 'string' ? { orgId: search.orgId } : {};
  },
});

const groupForm = createForm<Org>({
  defaultValue: {
    id: '',
    type: 'group',
    name: '',
    parentOrgId: '',
  },
  validations: {
    name: { required: (x) => !!x },
    parentOrgId: { required: (x) => !!x },
  },
});

function GroupEditor() {
  const t = useTranslator();
  const { id: groupId } = Route.useParams();
  const { orgId } = Route.useSearch();
  const { hasClaim } = useAuth();
  const canCreate = useCanCreateInTenant('orgs/create');
  const isNew = groupId === 'new';

  // A claim held in an organization applies in its groups, so asking in the group's own scope covers both
  // its administrators and those of the organization it belongs to. Creating one is a right held in the
  // organization that will own it, which is why a group admin cannot create a sibling of their own group.
  const readonly = isNew ? !(orgId ? hasClaim('orgs/create', orgId) : canCreate) : !hasClaim('orgs/write', groupId);

  return (
    <ItemEditor
      domain="groups"
      api={orgApi}
      readonly={readonly}
      deletable={!isNew && hasClaim('orgs/delete', groupId)}
      form={groupForm}
      name={(group) => group.name}
      formProps={{
        defaultValue: {
          id: '',
          type: 'group',
          name: '',
          parentOrgId: orgId ?? '',
        },
      }}
      footer={
        <div
          css={{
            marginTop: 'var(--mantine-spacing-xl)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--mantine-spacing-md)',
          }}
        >
          <LoadingBoundary>{!isNew && <OrgUserList type="group" orgId={groupId} />}</LoadingBoundary>
        </div>
      }
    >
      <LoadingBoundary>
        <groupForm.Field
          name="parentOrgId"
          render={(props) => (
            <OrgSelect
              {...props}
              value={props.value ?? undefined}
              label={t('groups.properties.org.label')}
              withAsterisk
              // Fixed once the group exists, and preselected when coming from the organization itself
              readOnly={!isNew || !!orgId}
              filterOptions={(org: Org) => org.type === 'tenant' && hasClaim('orgs/create', org.id)}
              searchable
              placeholder={t('common.select')}
            />
          )}
        />
      </LoadingBoundary>

      <groupForm.Field
        name="name"
        render={(props) => (
          <TextInput
            {...props}
            value={props.value ?? ''}
            label={t('groups.properties.name.label')}
            withAsterisk
            autoComplete="off"
            spellCheck={false}
          />
        )}
      />
    </ItemEditor>
  );
}
